import { useState, useMemo, memo, useCallback } from 'react'
import { Search, Plus, Minus, X, ChevronLeft } from 'lucide-react'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { Skeleton } from '@/components/ui/Skeleton'
import { useBookingMenuCategories, useBookingMenuItems, useAddPreOrderItem } from '@/lib/booking/preOrderAdapter'
import { formatPOSMoney, usePOSCurrency } from '@/lib/pos/currency'
import { searchMenuItems } from '@/lib/pos/menuSearch'
import type { MenuItem, MenuItemVariant } from '@/lib/types/pos'

// ── Types ─────────────────────────────────────────────────────────────────────

interface CartLine {
  item:     MenuItem
  variant:  MenuItemVariant | null
  quantity: number
  weightGrams?: number
  notes:    string
  key:      string
}

function makeKey(itemDocId: string, variantDocId?: string) {
  return `${itemDocId}::${variantDocId ?? ''}`
}

function formatWeight(weightGrams: number) {
  return weightGrams % 1000 === 0 ? `${weightGrams / 1000} kg` : `${weightGrams} g`
}

// ── Memoized item tile ─────────────────────────────────────────────────────────

const ItemTile = memo(function ItemTile({
  item,
  inCart,
  isActive,
  money,
  onTap,
}: {
  item:     MenuItem
  inCart:   boolean
  isActive: boolean
  money:    (amount: number) => string
  onTap:    (item: MenuItem) => void
}) {
  return (
    <button
      type="button"
      disabled={!item.isAvailable}
      onClick={() => item.isAvailable && onTap(item)}
      className={[
        'relative text-left rounded-[var(--radius-md)] border p-2.5 transition-colors',
        !item.isAvailable
          ? 'opacity-40 cursor-not-allowed border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)]'
          : isActive
          ? 'border-[var(--color-accent-amber)] bg-[var(--color-accent-amber)]/10 ring-1 ring-[var(--color-accent-amber)]/30'
          : inCart
          ? 'border-[var(--color-accent-amber)]/50 bg-[var(--color-accent-amber)]/5'
          : 'border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] hover:border-[var(--color-border-strong)]',
      ].join(' ')}
    >
      {inCart && !isActive && (
        <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-[var(--color-accent-amber)]" />
      )}
      {item.category?.name && (
        <p className="mb-0.5 text-[10px] font-medium uppercase tracking-wide text-[var(--color-text-muted)] truncate">
          {item.category.name}
        </p>
      )}
      <p className="text-xs font-semibold text-[var(--color-text-primary)] leading-tight line-clamp-2 pr-2">
        {item.name}
      </p>
      <p className="text-xs text-[var(--color-text-secondary)] mt-1">{money(item.basePrice)}{item.soldByWeight ? '/kg' : ''}</p>
      {!item.isAvailable && (
        <p className="text-[10px] text-[var(--color-text-muted)] mt-0.5">Unavailable</p>
      )}
    </button>
  )
})

// ── Option picker (variant + qty + notes) ────────────────────────────────────

function OptionPicker({
  item,
  variant,
  qty,
  notes,
  onVariantChange,
  onQtyChange,
  weightInput,
  weightUnit,
  onWeightInputChange,
  onWeightUnitChange,
  onNotesChange,
  money,
  onConfirm,
  onCancel,
}: {
  item:            MenuItem
  variant:         MenuItemVariant | null
  qty:             number
  notes:           string
  onVariantChange: (v: MenuItemVariant) => void
  onQtyChange:     (q: number) => void
  weightInput:     string
  weightUnit:      'g' | 'kg'
  onWeightInputChange: (value: string) => void
  onWeightUnitChange: (unit: 'g' | 'kg') => void
  onNotesChange:   (n: string) => void
  money:            (amount: number) => string
  onConfirm:       () => void
  onCancel:        () => void
}) {
  const hasVariants   = item.variants.length > 0
  const variantMissing = hasVariants && !variant
  const baseUnitPrice = item.basePrice + (variant?.priceDelta ?? 0)
  const weightGrams = Math.round((Number(weightInput) || 0) * (weightUnit === 'kg' ? 1000 : 1))
  const unitPrice = item.soldByWeight ? baseUnitPrice * weightGrams / 1000 : baseUnitPrice
  const linePrice = unitPrice * qty

  return (
    <div className="flex flex-col h-full">
      <div className="shrink-0 px-3 pt-3 pb-2 border-b border-[var(--color-border-subtle)]">
        <button
          type="button"
          onClick={onCancel}
          className="flex items-center gap-1 text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)] mb-2"
        >
          <ChevronLeft className="h-3.5 w-3.5" /> Back
        </button>
        <p className="text-sm font-semibold text-[var(--color-text-primary)] leading-tight">{item.name}</p>
        <p className="text-base font-bold text-[var(--color-accent-amber)] mt-0.5">
          {money(linePrice)}{item.soldByWeight ? ` · ${weightGrams} g` : ''}
        </p>
      </div>

      <div className="flex-1 overflow-y-auto px-3 py-2 space-y-4">
        {hasVariants && (
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-[var(--color-text-muted)] mb-1.5">
              Choose option <span className="text-[var(--color-accent-amber)]">*</span>
            </p>
            <div className="space-y-1">
              {item.variants.map((v) => (
                <button
                  type="button"
                  key={v.documentId}
                  onClick={() => onVariantChange(v)}
                  className={[
                    'w-full flex items-center justify-between px-2.5 py-2 rounded-[var(--radius-sm)] border text-xs transition-colors',
                    variant?.documentId === v.documentId
                      ? 'border-[var(--color-accent-amber)] bg-[var(--color-accent-amber)]/10 text-[var(--color-text-primary)]'
                      : 'border-[var(--color-border-default)] text-[var(--color-text-secondary)] hover:border-[var(--color-border-strong)]',
                  ].join(' ')}
                >
                  <span className="font-medium">{v.name}</span>
                  <span>{money(item.basePrice + v.priceDelta)}{item.soldByWeight ? '/kg' : ''}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-[var(--color-text-muted)] mb-1.5">
            Note (optional)
          </p>
          <textarea
            value={notes}
            onChange={(e) => onNotesChange(e.target.value)}
            placeholder="e.g. no onions, allergies…"
            rows={2}
            className="w-full px-2.5 py-2 text-xs rounded-[var(--radius-sm)] bg-[var(--color-bg-elevated)] border border-[var(--color-border-default)] text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] focus:outline-none focus:ring-1 focus:ring-[var(--color-accent-amber)]/40 resize-none"
          />
        </div>

        {item.soldByWeight ? (
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-[var(--color-text-muted)] mb-1.5">Weight · {money(baseUnitPrice)}/kg</p>
            <div className="flex items-center gap-2">
              <input type="number" min="0.001" step={weightUnit === 'kg' ? '0.001' : '1'} value={weightInput} onChange={(event) => onWeightInputChange(event.target.value)} className="h-10 min-w-0 flex-1 rounded-[var(--radius-sm)] border border-[var(--color-border-default)] bg-[var(--color-bg-elevated)] px-3 text-sm text-[var(--color-text-primary)]" />
              <div className="flex rounded-[var(--radius-sm)] bg-[var(--color-bg-elevated)] p-1">
                {(['g', 'kg'] as const).map((unit) => <button key={unit} type="button" onClick={() => onWeightUnitChange(unit)} className={`rounded px-3 py-1.5 text-xs font-semibold ${weightUnit === unit ? 'bg-white text-[var(--color-text-primary)] shadow-sm' : 'text-[var(--color-text-muted)]'}`}>{unit}</button>)}
              </div>
            </div>
          </div>
        ) : (
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-[var(--color-text-muted)] mb-1.5">Quantity</p>
            <div className="flex items-center gap-3">
              <button type="button" onClick={() => onQtyChange(Math.max(1, qty - 1))} className="h-7 w-7 rounded-full bg-[var(--color-bg-elevated)] border border-[var(--color-border-subtle)] flex items-center justify-center hover:bg-[var(--color-bg-surface)]"><Minus className="h-3 w-3" /></button>
              <span className="text-base font-bold text-[var(--color-text-primary)] min-w-[1.5rem] text-center">{qty}</span>
              <button type="button" onClick={() => onQtyChange(qty + 1)} className="h-7 w-7 rounded-full bg-[var(--color-accent-amber)] flex items-center justify-center"><Plus className="h-3 w-3 text-black" /></button>
            </div>
          </div>
        )}
      </div>

      <div className="shrink-0 px-3 py-2.5 border-t border-[var(--color-border-subtle)] space-y-1.5">
        {variantMissing && (
          <p className="text-[11px] text-[var(--color-accent-amber)] text-center">
            Please choose an option above
          </p>
        )}
        <Button className="w-full" size="sm" disabled={variantMissing} onClick={onConfirm}>
          Add {qty > 1 ? `×${qty} ` : ''}— {money(unitPrice * qty)}
        </Button>
      </div>
    </div>
  )
}

// ── Cart panel ────────────────────────────────────────────────────────────────

function CartPanel({
  cart,
  onAdjust,
  onRemove,
  money,
  onSubmit,
  submitting,
}: {
  cart:       Map<string, CartLine>
  onAdjust:   (key: string, delta: number) => void
  onRemove:   (key: string) => void
  money:      (amount: number) => string
  onSubmit:   () => void
  submitting: boolean
}) {
  const lines      = Array.from(cart.values())
  const totalItems = lines.reduce((s, l) => s + l.quantity, 0)

  return (
    <div className="flex flex-col h-full">
      <div className="shrink-0 px-3 py-2.5 border-b border-[var(--color-border-subtle)]">
        <p className="text-xs font-semibold text-[var(--color-text-muted)] uppercase tracking-wider">
          Pre-order summary
        </p>
      </div>

      <div className="flex-1 overflow-y-auto py-1">
        {lines.length === 0 ? (
          <div className="flex items-center justify-center h-full text-xs text-[var(--color-text-muted)] text-center px-4">
            Tap an item to add it
          </div>
        ) : (
          lines.map((line) => (
            <div key={line.key} className="px-2 py-2 border-b border-[var(--color-border-subtle)]/40 last:border-0">
              <div className="grid gap-x-2 items-center" style={{ gridTemplateColumns: '1.75rem 1fr' }}>
                <div className="flex flex-col items-center gap-0.5">
                  <button
                    type="button"
                    onClick={() => onAdjust(line.key, 1)}
                    disabled={line.weightGrams != null}
                    className="h-5 w-5 rounded bg-[var(--color-accent-amber)] flex items-center justify-center"
                  >
                    <Plus className="h-2.5 w-2.5 text-black" />
                  </button>
                  <span className="text-xs font-bold text-[var(--color-text-primary)] leading-none py-0.5 text-center">
                    {line.weightGrams != null ? formatWeight(line.weightGrams) : line.quantity}
                  </span>
                  <button
                    type="button"
                    onClick={() => onAdjust(line.key, -1)}
                    className="h-5 w-5 rounded bg-[var(--color-bg-elevated)] border border-[var(--color-border-subtle)] flex items-center justify-center hover:bg-[var(--color-bg-surface)]"
                  >
                    <Minus className="h-2.5 w-2.5" />
                  </button>
                </div>

                <div className="min-w-0">
                  <div className="flex items-center gap-1">
                    <p className="flex-1 text-xs font-semibold text-[var(--color-text-primary)] leading-tight">
                      {line.item.name}
                    </p>
                    <span className="text-xs text-[var(--color-text-muted)] shrink-0">
                      {money(line.item.soldByWeight && line.weightGrams != null
                        ? (line.item.basePrice + (line.variant?.priceDelta ?? 0)) * line.weightGrams / 1000
                        : (line.item.basePrice + (line.variant?.priceDelta ?? 0)) * line.quantity)}
                    </span>
                    <button
                      type="button"
                      onClick={() => onRemove(line.key)}
                      aria-label={`Remove ${line.item.name}`}
                      className="shrink-0 h-5 w-5 rounded flex items-center justify-center text-[var(--color-text-muted)] hover:text-[var(--color-accent-red)] transition-colors"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                  {line.variant && (
                    <p className="text-[10px] text-[var(--color-text-muted)] mt-0.5">{line.variant.name}</p>
                  )}
                  {line.notes && (
                    <p className="text-[10px] text-[var(--color-accent-amber)] italic mt-0.5">{line.notes}</p>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="shrink-0 border-t border-[var(--color-border-subtle)] px-3 py-2 flex justify-between items-center">
        <span className="text-xs text-[var(--color-text-muted)]">
          {totalItems > 0 ? `${totalItems} item${totalItems !== 1 ? 's' : ''}` : 'No items'}
        </span>
      </div>

      <div className="shrink-0 px-3 pb-3">
        <Button
          className="w-full"
          size="sm"
          disabled={totalItems === 0}
          loading={submitting}
          onClick={onSubmit}
        >
          Save pre-order
        </Button>
      </div>
    </div>
  )
}

// ── Main modal ────────────────────────────────────────────────────────────────

interface Props {
  open:              boolean
  onClose:           () => void
  bookingDocumentId: string
}

export function BookingPreOrderModal({ open, onClose, bookingDocumentId }: Props) {
  const currency = usePOSCurrency()
  const money = useMemo(() => (amount: number) => formatPOSMoney(amount, currency), [currency])
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null)
  const [search, setSearch]                         = useState('')
  const [cart, setCart]                             = useState<Map<string, CartLine>>(new Map())

  const [optionItem,    setOptionItem]    = useState<MenuItem | null>(null)
  const [optionVariant, setOptionVariant] = useState<MenuItemVariant | null>(null)
  const [optionQty,     setOptionQty]     = useState(1)
  const [optionWeight, setOptionWeight] = useState('500')
  const [optionWeightUnit, setOptionWeightUnit] = useState<'g' | 'kg'>('g')
  const [optionNotes,   setOptionNotes]   = useState('')

  const { data: catsResp, isLoading: catsLoading } = useBookingMenuCategories()
  const categories = (catsResp as any)?.data ?? []

  const { data: itemsResp, isLoading: categoryItemsLoading } = useBookingMenuItems(selectedCategoryId)
  const categoryItems: MenuItem[] = (itemsResp as any)?.data ?? []
  const { data: allItemsResp, isLoading: allItemsLoading } = useBookingMenuItems()
  const allItems: MenuItem[] = (allItemsResp as any)?.data ?? []
  const isSearching = search.trim().length > 0
  const itemsLoading = isSearching ? allItemsLoading : categoryItemsLoading

  const addPreOrder = useAddPreOrderItem()

  const filteredItems = useMemo(() => {
    return isSearching ? searchMenuItems(allItems, search) : categoryItems
  }, [allItems, categoryItems, isSearching, search])

  function cartQtyFor(item: MenuItem) {
    let total = 0
    for (const [k, line] of cart) {
      if (k.startsWith(item.documentId + '::')) total += line.quantity
    }
    return total
  }

  const adjustQty = useCallback((key: string, delta: number) => {
    setCart((prev) => {
      const next = new Map(prev)
      const line = next.get(key)
      if (!line) return prev
      const newQty = line.quantity + delta
      if (newQty <= 0) next.delete(key)
      else next.set(key, { ...line, quantity: newQty })
      return next
    })
  }, [])

  const removeLine = useCallback((key: string) => {
    setCart((prev) => { const next = new Map(prev); next.delete(key); return next })
  }, [])

  const handleItemTap = useCallback((item: MenuItem) => {
    if (!item.isAvailable) return
    if (item.variants.length > 0 || item.soldByWeight) {
      setOptionItem(item)
      setOptionVariant(null)
      setOptionQty(1)
      setOptionWeight('500')
      setOptionWeightUnit('g')
      setOptionNotes('')
    } else {
      const key = makeKey(item.documentId)
      setCart((prev) => {
        const next = new Map(prev)
        const existing = next.get(key)
        if (existing) next.set(key, { ...existing, quantity: existing.quantity + 1 })
        else next.set(key, { key, item, variant: null, quantity: 1, notes: '' })
        return next
      })
    }
  }, [])

  const handleOptionConfirm = useCallback(() => {
    if (!optionItem) return
    const weightGrams = optionItem.soldByWeight
      ? Math.round((Number(optionWeight) || 0) * (optionWeightUnit === 'kg' ? 1000 : 1))
      : undefined
    if (optionItem.soldByWeight && (!weightGrams || weightGrams < 1 || weightGrams > 10000000)) return
    const key = optionItem.soldByWeight
      ? `${makeKey(optionItem.documentId, optionVariant?.documentId)}::${weightGrams}g::${Date.now()}`
      : makeKey(optionItem.documentId, optionVariant?.documentId)
    setCart((prev) => {
      const next     = new Map(prev)
      const existing = next.get(key)
      if (existing) next.set(key, { ...existing, quantity: existing.quantity + optionQty })
      else next.set(key, { key, item: optionItem, variant: optionVariant, quantity: optionItem.soldByWeight ? 1 : optionQty, weightGrams, notes: optionNotes })
      return next
    })
    setOptionItem(null)
  }, [optionItem, optionVariant, optionQty, optionNotes, optionWeight, optionWeightUnit])

  const handleSubmit = async () => {
    await Promise.all(
      Array.from(cart.values()).map((line) =>
        addPreOrder.mutateAsync({
          bookingDocumentId,
          menuItemDocumentId: line.item.documentId,
          variantDocumentId:  line.variant?.documentId ?? null,
          quantity:           line.quantity,
          weightGrams:        line.weightGrams,
          course:             (line.item as any).defaultCourse ?? 'main',
          notes:              line.notes || undefined,
        }),
      ),
    )
    handleClose()
  }

  const handleClose = () => {
    setCart(new Map())
    setSearch('')
    setSelectedCategoryId(null)
    setOptionItem(null)
    onClose()
  }

  return (
    <Modal open={open} onClose={handleClose} title="Add Pre-order Items" size="xl" className="max-h-[92vh]">
      <div className="flex flex-1 min-h-0 -mx-4 -mb-4 flex-col divide-y divide-[var(--color-border-subtle)] sm:-mx-6 sm:-mb-6 md:flex-row md:divide-x md:divide-y-0">

        {/* Column 1: Categories */}
        <div className="max-h-32 shrink-0 overflow-y-auto md:max-h-none md:w-40">
          <div className="grid grid-cols-2 gap-1 p-2 sm:grid-cols-3 md:block md:space-y-0.5">
            <button
              type="button"
              onClick={() => {
                setSelectedCategoryId(null)
                if (isSearching) setSearch('')
              }}
              className={[
                'w-full text-left px-3 py-2 rounded-[var(--radius-sm)] text-xs font-medium transition-colors',
                selectedCategoryId === null || isSearching
                  ? 'bg-[var(--color-bg-elevated)] text-[var(--color-text-primary)]'
                  : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-elevated)]/50',
              ].join(' ')}
            >
              All items
            </button>
            {catsLoading
              ? Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} variant="text" className="mx-2 my-1" />)
              : categories.map((cat: any) => (
                  <button
                    type="button"
                    key={cat.documentId}
                    onClick={() => {
                      setSelectedCategoryId(cat.documentId)
                      if (isSearching) setSearch('')
                    }}
                    className={[
                      'w-full text-left px-3 py-2 rounded-[var(--radius-sm)] text-xs font-medium transition-colors',
                      selectedCategoryId === cat.documentId && !isSearching
                        ? 'bg-[var(--color-bg-elevated)] text-[var(--color-text-primary)]'
                        : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-elevated)]/50',
                    ].join(' ')}
                  >
                    {cat.name}
                  </button>
                ))}
          </div>
        </div>

        {/* Column 2: Item grid */}
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          <div className="shrink-0 px-3 py-2.5 border-b border-[var(--color-border-subtle)]">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[var(--color-text-muted)]" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search items and categories…"
                className="w-full h-8 pl-8 pr-3 rounded-[var(--radius-sm)] bg-[var(--color-bg-elevated)] border border-[var(--color-border-default)] text-xs text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] focus:outline-none focus:ring-1 focus:ring-[var(--color-accent-amber)]/30"
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-2">
            {itemsLoading ? (
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {Array.from({ length: 9 }).map((_, i) => <Skeleton key={i} variant="rectangular" height={72} />)}
              </div>
            ) : filteredItems.length === 0 ? (
              <p className="text-xs text-[var(--color-text-muted)] text-center py-8">No items found.</p>
            ) : (
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {filteredItems.map((item) => (
                  <ItemTile
                    key={item.documentId}
                    item={item}
                    inCart={cartQtyFor(item) > 0}
                    isActive={optionItem?.documentId === item.documentId}
                    money={money}
                    onTap={handleItemTap}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Column 3: Option picker OR cart */}
        <div className="max-h-64 shrink-0 overflow-hidden md:max-h-none md:w-60">
          {optionItem ? (
            <OptionPicker
              item={optionItem}
              variant={optionVariant}
              qty={optionQty}
              notes={optionNotes}
              money={money}
              weightInput={optionWeight}
              weightUnit={optionWeightUnit}
              onWeightInputChange={setOptionWeight}
              onWeightUnitChange={setOptionWeightUnit}
              onVariantChange={setOptionVariant}
              onQtyChange={setOptionQty}
              onNotesChange={setOptionNotes}
              onConfirm={handleOptionConfirm}
              onCancel={() => setOptionItem(null)}
            />
          ) : (
            <CartPanel
              cart={cart}
              onAdjust={adjustQty}
              onRemove={removeLine}
              money={money}
              onSubmit={handleSubmit}
              submitting={addPreOrder.isPending}
            />
          )}
        </div>

      </div>
    </Modal>
  )
}
